import React from 'react';
import { Clock, MapPin, User, PlayCircle, AlertCircle, CheckCircle } from 'lucide-react';
interface Job {
  id: number;
  timeStart: string;
  timeEnd: string;
  address: string;
  city: string;
  host: string;
  type: 'Pulizia Standard' | 'Pulizia Profonda' | 'Check-out';
  cleaner: {
    name: string;
    avatarColor: string;
    initials: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'issue';
  earnings: string;
  lat: number;
  lng: number;
}
interface JobHoverTooltipProps {
  job: Job;
  position: {
    x: number;
    y: number;
  };
  visible: boolean;
}
export function JobHoverTooltip({
  job,
  position,
  visible
}: JobHoverTooltipProps) {
  if (!visible) return null;
  const getStatusColor = (status: Job['status']) => {
    switch (status) {
      case 'in-progress':
        return 'text-blue-600 bg-blue-50 border-blue-100';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-100';
      case 'issue':
        return 'text-amber-600 bg-amber-50 border-amber-100';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };
  const getStatusIcon = (status: Job['status']) => {
    switch (status) {
      case 'in-progress':
        return <PlayCircle size={12} data-id="element-711" />;
      case 'completed':
        return <CheckCircle size={12} data-id="element-712" />;
      case 'issue':
        return <AlertCircle size={12} data-id="element-713" />;
      default:
        return <Clock size={12} data-id="element-714" />;
    }
  };
  const getStatusLabel = (status: Job['status']) => {
    switch (status) {
      case 'in-progress':
        return 'In Corso';
      case 'completed':
        return 'Completato';
      case 'issue':
        return 'Problema';
      default:
        return 'Programmato';
    }
  };
  const getTypeColor = (type: Job['type']) => {
    switch (type) {
      case 'Pulizia Profonda':
        return 'text-purple-700 bg-purple-50 border-purple-100';
      case 'Check-out':
        return 'text-indigo-700 bg-indigo-50 border-indigo-100';
      default:
        return 'text-teal-700 bg-teal-50 border-teal-100';
    }
  };
  return <div className="absolute z-[1000] pointer-events-none transition-opacity duration-150" style={{
    left: position.x + 20,
    top: position.y,
    transform: 'translateY(-50%)',
    opacity: visible ? 1 : 0
  }} data-id="element-715">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-[240px] relative" data-id="element-716">
        {/* Arrow pointing left */}
        <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-white border-l border-b border-gray-200 transform rotate-45" style={{
        boxShadow: '-2px 2px 2px -2px rgba(0,0,0,0.05)'
      }} data-id="element-717" />

        <div className="relative z-10 space-y-2" data-id="element-718">
          {/* Header: Type & Earnings */}
          <div className="flex justify-between items-start" data-id="element-719">
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border text-gray-600 bg-gray-50 border-gray-200" data-id="element-720">
              {job.type}
            </span>
            <span className="text-sm font-bold text-navy" data-id="element-721">{job.earnings}</span>
          </div>

          {/* Address */}
          <div className="flex items-start gap-2" data-id="element-722">
            <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-723" />
            <div data-id="element-724">
              <p className="text-xs font-bold text-navy leading-tight" data-id="element-725">
                {job.address}
              </p>
              <p className="text-[10px] text-gray-500" data-id="element-726">{job.city}</p>
            </div>
          </div>

          {/* Time */}
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-100" data-id="element-727">
            <Clock size={12} className="text-gray-400" data-id="element-728" />
            <span className="font-medium" data-id="element-729">
              {job.timeStart} - {job.timeEnd}
            </span>
          </div>

          <div className="border-t border-gray-100 my-2" data-id="element-730" />

          {/* Footer: Cleaner & Status */}
          <div className="flex justify-between items-center" data-id="element-731">
            <div className="flex items-center gap-2" data-id="element-732">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${job.cleaner.avatarColor}`} data-id="element-733">
                {job.cleaner.initials}
              </div>
              <span className="text-[10px] font-medium text-gray-600 truncate max-w-[80px]" data-id="element-734">
                {job.cleaner.name}
              </span>
            </div>
            <div className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${getStatusColor(job.status)}`} data-id="element-735">
              {getStatusIcon(job.status)}
              {getStatusLabel(job.status)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}